import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col, Typography, Tag, Spin, Button, Divider, Space, Alert } from 'antd';
import { 
  BuildOutlined, 
  GlobalOutlined, 
  NumberOutlined, 
  CalendarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  HomeOutlined
} from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

interface DppData {
  dpp_id: string;
  product_name: string;
  gtin: string;
  mpn: string;
  sku: string;
  description: string;
  organisation_name: string;
  brand_name: string;
  status: string;
  completeness_score: number;
  compliance_score: number;
  created_at: string;
  updated_at: string;
  // Add more fields as needed
}

const DppViewer: React.FC = () => {
  const { dppId } = useParams<{ dppId: string }>();
  const navigate = useNavigate();
  const [dppData, setDppData] = useState<DppData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDppData = async () => {
      if (!dppId) return;

      try {
        setLoading(true);
        // Fetch DPP data from API
        const response = await fetch(`/api/qr/dpps/${dppId}/public`);
        
        if (!response.ok) {
          throw new Error('DPP nicht gefunden');
        }

        const data = await response.json();
        setDppData(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Unbekannter Fehler');
      } finally {
        setLoading(false);
      }
    };

    fetchDppData();
  }, [dppId]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published': return 'green';
      case 'draft': return 'orange';
      case 'in_review': return 'blue';
      default: return 'default';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 95) return '#52c41a';
    if (score >= 80) return '#faad14';
    return '#ff4d4f';
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !dppData) {
    return (
      <div style={{ maxWidth: '800px', margin: '50px auto', padding: '20px' }}>
        <Alert
          message="DPP nicht gefunden"
          description={`Der Digital Product Passport mit der ID "${dppId}" konnte nicht gefunden werden.`}
          type="error"
          showIcon
          action={
            <Button type="primary" onClick={() => navigate('/')}>
              Zurück zur Startseite
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      padding: '10px'
    }}>
      <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '16px', 
          marginBottom: '16px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)'
        }}>
          <Row align="middle" justify="space-between" gutter={[16, 16]}>
            <Col xs={24} sm={12}>
              <Space direction="vertical" size="small">
                <Button 
                  icon={<HomeOutlined />} 
                  onClick={() => navigate('/')}
                  type="text"
                  size="small"
                >
                  Zurück
                </Button>
                <Title level={3} style={{ margin: 0, color: '#1a1a1a', fontSize: '18px' }}>
                  Digital Product Passport
                </Title>
              </Space>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'right' }}>
              <Tag color={getStatusColor(dppData.status)} style={{ fontSize: '12px', padding: '2px 8px' }}>
                {dppData.status.toUpperCase()}
              </Tag>
            </Col>
          </Row>
        </div>

        {/* Main Content */}
        <Row gutter={[16, 16]}>
          {/* Product Information */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <NumberOutlined />
                  <span style={{ fontSize: '14px' }}>Produktinformationen</span>
                </Space>
              }
              size="small"
              style={{ height: '100%', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Produktname:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Title level={5} style={{ margin: 0, color: '#1a1a1a', fontSize: '16px' }}>
                      {dppData.product_name}
                    </Title>
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>GTIN:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text code style={{ fontSize: '12px' }}>{dppData.gtin}</Text>
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>MPN:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text code style={{ fontSize: '12px' }}>{dppData.mpn}</Text>
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>SKU:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text code style={{ fontSize: '12px' }}>{dppData.sku}</Text>
                  </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Beschreibung:</Text>
                  <div style={{ marginTop: '4px' }}>
                    <Paragraph style={{ margin: 0, lineHeight: '1.4', fontSize: '12px' }}>
                      {dppData.description}
                    </Paragraph>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Organisation Information */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <BuildOutlined />
                  <span style={{ fontSize: '14px' }}>Organisation</span>
                </Space>
              }
              size="small"
              style={{ height: '100%', boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Organisation:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Title level={5} style={{ margin: 0, color: '#1a1a1a', fontSize: '16px' }}>
                      {dppData.organisation_name}
                    </Title>
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Marke:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text style={{ fontSize: '12px' }}>{dppData.brand_name}</Text>
                  </div>
                </div>

                <Divider style={{ margin: '12px 0' }} />

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>DPP ID:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text code copyable style={{ fontSize: '10px' }}>
                      {dppData.dpp_id}
                    </Text>
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Status:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Tag color={getStatusColor(dppData.status)} style={{ fontSize: '10px' }}>
                      {dppData.status}
                    </Tag>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Quality Scores */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <CheckCircleOutlined />
                  <span style={{ fontSize: '14px' }}>Qualitätsmetriken</span>
                </Space>
              }
              size="small"
              style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong style={{ color: '#666', fontSize: '12px' }}>Vollständigkeit</Text>
                    <Text strong style={{ color: getScoreColor(dppData.completeness_score), fontSize: '12px' }}>
                      {dppData.completeness_score}%
                    </Text>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '3px',
                    marginTop: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${dppData.completeness_score}%`,
                      height: '100%',
                      backgroundColor: getScoreColor(dppData.completeness_score),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>

                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Text strong style={{ color: '#666', fontSize: '12px' }}>Compliance</Text>
                    <Text strong style={{ color: getScoreColor(dppData.compliance_score), fontSize: '12px' }}>
                      {dppData.compliance_score}%
                    </Text>
                  </div>
                  <div style={{ 
                    width: '100%', 
                    height: '6px', 
                    backgroundColor: '#f0f0f0', 
                    borderRadius: '3px',
                    marginTop: '4px',
                    overflow: 'hidden'
                  }}>
                    <div style={{
                      width: `${dppData.compliance_score}%`,
                      height: '100%',
                      backgroundColor: getScoreColor(dppData.compliance_score),
                      transition: 'width 0.3s ease'
                    }} />
                  </div>
                </div>
              </Space>
            </Card>
          </Col>

          {/* Timestamps */}
          <Col xs={24} lg={12}>
            <Card 
              title={
                <Space>
                  <CalendarOutlined />
                  <span style={{ fontSize: '14px' }}>Zeitstempel</span>
                </Space>
              }
              size="small"
              style={{ boxShadow: '0 4px 16px rgba(0,0,0,0.1)' }}
            >
              <Space direction="vertical" size="small" style={{ width: '100%' }}>
                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Erstellt am:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text style={{ fontSize: '11px' }}>{new Date(dppData.created_at).toLocaleString('de-DE')}</Text>
                  </div>
                </div>

                <div>
                  <Text strong style={{ color: '#666', fontSize: '12px' }}>Aktualisiert am:</Text>
                  <div style={{ marginTop: '2px' }}>
                    <Text style={{ fontSize: '11px' }}>{new Date(dppData.updated_at).toLocaleString('de-DE')}</Text>
                  </div>
                </div>
              </Space>
            </Card>
          </Col>
        </Row>

        {/* Footer */}
        <div style={{ 
          background: 'white', 
          borderRadius: '12px', 
          padding: '12px', 
          marginTop: '16px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(0,0,0,0.1)'
        }}>
          <Text type="secondary" style={{ fontSize: '10px' }}>
            Digital Product Passport - Powered by DiKItal Solutions
          </Text>
        </div>
      </div>
    </div>
  );
};

export default DppViewer;
